import { http } from '@/lib/http';
import {
  IntegratorAdminListSchema,
  IntegratorAdminSchema,
  IntegratorPublicListSchema,
  IntegratorSessionSchema,
  type IntegratorAdmin,
  type IntegratorAdminList,
  type IntegratorCreate,
  type IntegratorPublicList,
  type IntegratorSession,
  type IntegratorUpdate,
} from './schemas';

// Helper: `http.get` unwrapp del envelope. Cuando el back devuelve un
// array plano, http lo re-envuelve en shape paginated `{data,total,skip,take}`.
// Para las listas usamos ese shape; para arrays simples parsemos directo.
function unwrapArray(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in raw &&
    Array.isArray((raw as { data: unknown }).data)
  ) {
    return (raw as { data: unknown[] }).data;
  }
  return [];
}

// GET /integrators — user-facing. Filtered por role del caller. Gate
// `products:read` en el back. Response: array plano de items PUBLIC.
export async function listIntegrators(): Promise<IntegratorPublicList> {
  const raw = await http.get<unknown>('/integrators');
  return IntegratorPublicListSchema.parse(unwrapArray(raw));
}

// GET /admin/integrators — admin. Gate `integrations:manage`. Incluye rows
// inactivas y campos de configuración. Peer confirmó (2026-08-26): NO
// paginated. Response array plano después del envelope + unwrap.
export async function listAdminIntegrators(): Promise<IntegratorAdminList> {
  const raw = await http.get<unknown>('/admin/integrators');
  return IntegratorAdminListSchema.parse(unwrapArray(raw));
}

// El back del release 2026-08-26 tipa `Role.id` como `Int` y su DTO exige
// `roleIds: number[]` estricto (peer confirmo el error path). El front
// normaliza los ids a string via `IdSchema` para no mezclar shapes en el
// read; convertimos aca al enviar. Falla noisy si algun id no es numerico
// -- prefiero un throw explicito que un `NaN` en el body.
function toNumericRoleIds(ids: readonly string[]): number[] {
  return ids.map((raw) => {
    const n = Number(raw);
    if (!Number.isFinite(n) || !Number.isInteger(n)) {
      throw new Error(
        `roleId no numerico: ${JSON.stringify(raw)}. El back solo acepta enteros.`,
      );
    }
    return n;
  });
}

export async function createIntegrator(
  body: IntegratorCreate,
): Promise<IntegratorAdmin> {
  const payload = { ...body, roleIds: toNumericRoleIds(body.roleIds ?? []) };
  const raw = await http.post<unknown>('/admin/integrators', payload);
  return IntegratorAdminSchema.parse(raw);
}

export async function updateIntegrator(
  id: string,
  body: IntegratorUpdate,
): Promise<IntegratorAdmin> {
  const payload =
    body.roleIds !== undefined
      ? { ...body, roleIds: toNumericRoleIds(body.roleIds) }
      : body;
  const raw = await http.patch<unknown>(
    `/admin/integrators/${encodeURIComponent(id)}`,
    payload,
  );
  return IntegratorAdminSchema.parse(raw);
}

export async function deleteIntegrator(id: string): Promise<void> {
  await http.delete(`/admin/integrators/${encodeURIComponent(id)}`, {
    parseResponse: 'none',
  });
}

// POST /integrators/:slug/session — emite ticket single-use TTL 2min.
// 403 si el caller no tiene el rol para ese integrador; 404 si el slug es
// unknown o inactivo. Consumo one-shot en IntegratorFrame.
export async function createIntegratorSession(
  slug: string,
): Promise<IntegratorSession> {
  const raw = await http.post<unknown>(
    `/integrators/${encodeURIComponent(slug)}/session`,
    {},
  );
  return IntegratorSessionSchema.parse(raw);
}
